import { useState, useEffect, Fragment, useMemo, useRef } from "react";
import {
  ChevronLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  ShareIcon,
} from "@heroicons/react/outline";
import { useParams } from "react-router-dom";
import Loader from "../../components/loader/Loader";
import {
  Dialog,
  Transition,
  Listbox,
  RadioGroup,
  Combobox,
} from "@headlessui/react";
import * as api from "./apiService";
import { toast } from "react-toastify";

import { fetchUser, selectUser } from "../../routes/userProfile/userSlice";
import { useAppSelector, useAppDispatch } from "../../app/hooks";

const groupOptions = [
  { name: "By Aircraft" },
  { name: "By Aircraft Category" },
  { name: "By Service" },
];

const XMarkIcon = ({ className = "w-6 h-6" }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
};

const moneySanitize = (v) =>
  String(v ?? "")
    .replace(/[^0-9.]/g, "")
    .replace(/(\..*)\./g, "$1");

const csvEscape = (value) => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  // If it contains comma, quote, or newline, wrap in quotes and escape quotes
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadTextFile = (filename, text, mime = "text/csv;charset=utf-8;") => {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const formatDateMMDDYYYY = (d = new Date()) => {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
};

// Optional: keep numbers raw, but if you want currency in CSV, switch to true.
const priceForCsv = (raw, asCurrency = false) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const n = Number(raw);
  if (Number.isNaN(n)) return "";
  if (!asCurrency) return n.toFixed(2);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const formatCurrency = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const n = Number(raw);
  if (Number.isNaN(n)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const MagnifyingGlassIcon = ({ className = "w-5 h-5" }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
      />
    </svg>
  );
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// ===== helpers =====

// ===== Editable cell for preview table =====
function EditableCell({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  useEffect(() => {
    if (editing) setTimeout(() => inputRef.current?.focus(), 0);
  }, [editing]);

  const commit = () => {
    const raw = moneySanitize(draft);
    setEditing(false);
    onChange(raw);
  };

  const display = formatCurrency(value);

  return (
    <td
      className="px-3 py-2 text-sm text-gray-700 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
      onClick={() => setEditing(true)}
      title="Click to edit"
    >
      {!editing ? (
        <div className="min-h-[20px]">
          {value === "" || value === null || value === undefined ? (
            <span className="text-gray-400">—</span>
          ) : (
            <span className="font-medium text-gray-900">{display}</span>
          )}
        </div>
      ) : (
        <div onClick={(e) => e.stopPropagation()}>
          <input
            ref={inputRef}
            value={draft} // show raw while editing
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setEditing(false);
                setDraft(value ?? "");
              }
            }}
            className="w-28 rounded-md border border-sky-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        </div>
      )}
    </td>
  );
}

function MultiSelectDropdown({
  label,
  placeholder,
  items,
  selected,
  setSelected,
  displayKey = "name",
  disabled = false,
  maxHeightClass = "max-h-64",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter((x) =>
      String(x?.[displayKey] ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [items, query, displayKey]);

  const isSelected = (item) => selected?.some((s) => s.id === item.id);

  const toggle = (item) => {
    if (disabled) return;
    if (isSelected(item)) {
      setSelected(selected.filter((s) => s.id !== item.id));
    } else {
      setSelected([...(selected || []), item]);
    }
    // keep dropdown open and let user keep searching/adding
    setQuery(""); // clear so they can search next item quickly (optional; remove if you prefer)
    inputRef.current?.focus();
  };

  // Close on click outside
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  return (
    <div ref={rootRef}>
      <label className="block text-sm font-semibold text-gray-800">
        {label}
      </label>

      <div className="relative mt-1">
        <div
          className={classNames(
            "relative w-full overflow-hidden rounded-lg border bg-white text-left shadow-sm",
            "border-gray-300 focus-within:ring-1 focus-within:ring-sky-500",
            disabled ? "opacity-60" : "",
          )}
        >
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            <MagnifyingGlassIcon className="h-4 w-4" />
          </div>

          <input
            ref={inputRef}
            value={query}
            disabled={disabled}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="w-full border-none py-2 pl-9 pr-10 text-sm text-gray-900 focus:ring-0"
          />

          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen((v) => !v)}
            className="absolute inset-y-0 right-0 flex items-center pr-2"
          >
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {open && (
          <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
            <div className={classNames("overflow-auto", maxHeightClass)}>
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No results
                </div>
              ) : (
                filtered.map((item) => {
                  const sel = isSelected(item);
                  return (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => toggle(item)}
                      className={classNames(
                        "w-full text-left px-3 py-2 text-sm flex items-center gap-2",
                        sel ? "bg-sky-50" : "hover:bg-gray-50",
                      )}
                    >
                      <span
                        className={classNames(
                          "inline-flex h-5 w-5 items-center justify-center rounded border",
                          sel
                            ? "border-sky-600 bg-sky-600 text-white"
                            : "border-gray-300 bg-white",
                        )}
                      >
                        {sel ? <CheckIcon className="h-4 w-4" /> : null}
                      </span>
                      <span
                        className={classNames(
                          "truncate",
                          sel ? "font-semibold text-gray-900" : "text-gray-900",
                        )}
                      >
                        {item?.[displayKey]}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {selected?.length ? (
              <div className="border-t border-gray-100 px-3 py-2">
                <button
                  type="button"
                  className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                  onClick={() => setSelected([])}
                >
                  Clear all
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Chips */}
      {selected?.length ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {selected.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700"
            >
              {s?.[displayKey]}
              <button
                type="button"
                className="text-gray-500 hover:text-gray-900"
                onClick={() =>
                  setSelected(selected.filter((x) => x.id !== s.id))
                }
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const ServicePrices = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const { pricePlanId } = useParams();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [totalAircraftTypes, setTotalAircraftTypes] = useState(0);
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [aircraftTypeSelected, setAircraftTypeSelected] = useState(null);
  const [aircraftSearchName, setAircraftSearchName] = useState("");

  const [totalAircraftCategories, setTotalAircraftCategories] = useState(0);
  const [aircraftCategories, setAircraftCategories] = useState([]);
  const [aircraftCategorySelected, setAircraftCategorySelected] =
    useState(null);
  const [aircraftCategorySearchName, setAircraftCategorySearchName] =
    useState("");

  const [pricePlans, setPricePlans] = useState([]);
  const [priceListing, setPriceListing] = useState([]);
  const [originalPriceListing, setOriginalPriceListing] = useState([]);

  const [updateLoading, setUpdateLoading] = useState(false);

  const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);

  const [serviceSearchName, setServiceSearchName] = useState("");
  const [services, setServices] = useState([]);
  const [serviceSelected, setServiceSelected] = useState(null);
  const [totalServices, setTotalServices] = useState(0);

  const [groupOptionSelected, setGroupOptionSelected] = useState(
    groupOptions[0],
  );
  const [pricePlansSelected, setPricePlansSelected] = useState([]);

  // ===== Export modal + preview state =====
  const [exportOpen, setExportOpen] = useState(false);
  const [exportModalLoading, setExportModalLoading] = useState(false);
  const [exportPreviewLoading, setExportPreviewLoading] = useState(false);

  const [exportCustomerName, setExportCustomerName] = useState("");
  const [exportAllServices, setExportAllServices] = useState([]);
  const [exportAllAircraftTypes, setExportAllAircraftTypes] = useState([]);
  const [exportServicesSelected, setExportServicesSelected] = useState([]);
  const [exportAircraftSelected, setExportAircraftSelected] = useState([]);

  const [previewMode, setPreviewMode] = useState(false);
  // exportPreview shape:
  // {
  //   price_list: {id, name},
  //   aircraft_types: [{id,name}],
  //   services: [{id,name}],
  //   prices: { [aircraftId]: { [serviceId]: "123.45" } }
  // }
  const [exportPreview, setExportPreview] = useState(null);

  const [activityOpen, setActivityOpen] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [priceListActivities, setPriceListActivities] = useState([]);

  const [activityPage, setActivityPage] = useState(1);
  const [activityPageSize, setActivityPageSize] = useState(10);
  const [activityTotalCount, setActivityTotalCount] = useState(0);

  useEffect(() => {
    dispatch(fetchUser());
  }, []);

  useEffect(() => {
    if (!activityOpen) return;

    loadPriceListActivity(activityPage);
  }, [activityOpen, activityPage, activityPageSize]);

  useEffect(() => {
    searchPricePlans();
  }, []);

  useEffect(() => {
    let timeoutID = setTimeout(() => {
      searchAircrafts();
    }, 500);
    return () => clearTimeout(timeoutID);
  }, [aircraftSearchName]);

  useEffect(() => {
    let timeoutID = setTimeout(() => {
      searchAircraftCategories();
    }, 500);
    return () => clearTimeout(timeoutID);
  }, [aircraftCategorySearchName]);

  useEffect(() => {
    let timeoutID = setTimeout(() => {
      searchServices();
    }, 500);
    return () => clearTimeout(timeoutID);
  }, [serviceSearchName]);

  const buildOverridesFromPreview = (preview) => {
    const overrides = [];
    for (const aircraft of preview.aircraft_types) {
      for (const svc of preview.services) {
        const raw = preview?.prices?.[aircraft.id]?.[svc.id] ?? "";
        // Send only filled values (or send all cells if you prefer)
        if (raw !== "") {
          overrides.push({
            aircraft_type_id: aircraft.id,
            service_id: svc.id,
            price: raw, // raw numeric string (e.g. "123.45")
          });
        }
      }
    }
    return overrides;
  };

  const loadPriceListActivity = async (page = activityPage) => {
    if (!pricePlansSelected?.length) {
      toast.error("Select a price list first.");
      return;
    }

    const priceListId = pricePlansSelected[0].id;

    setActivityLoading(true);

    try {
      const response = await api.getPriceListActivity(
        priceListId,
        page,
        activityPageSize,
      );

      setPriceListActivities(response.data.results || []);
      setActivityTotalCount(response.data.count || 0);
    } catch (error) {
      toast.error("Unable to load price list activity.");
    } finally {
      setActivityLoading(false);
    }
  };

  const openPriceListActivity = () => {
    if (!pricePlansSelected?.length) {
      toast.error("Select a price list first.");
      return;
    }

    setActivityPage(1);
    setActivityOpen(true);
  };

  const closePriceListActivity = () => {
    setActivityOpen(false);
  };

  const exportToPdf = async () => {
    try {
      const payload = {
        price_list_id: pricePlansSelected[0].id,
        aircraft_type_ids: exportAircraftSelected.map((a) => a.id),
        service_ids: exportServicesSelected.map((s) => s.id),
        customer_name: exportCustomerName,
        overrides: buildOverridesFromPreview(exportPreview),
      };

      const res = await api.exportPricingProposalPdf(payload);

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const safeCustomer = (exportCustomerName || "Customer")
        .replace(/[^\w\s-]/g, "")
        .trim();

      const a = document.createElement("a");
      a.href = url;
      a.download = `Pricing Proposal - ${safeCustomer}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to export PDF");
    }
  };

  const exportToCsv = () => {
    if (!exportPreview) {
      toast.error("No preview data to export.");
      return;
    }

    const customer =
      exportCustomerName?.trim() || "___________________________";
    const dateStr = formatDateMMDDYYYY(new Date());
    const pricePlanName = exportPreview.price_list?.name || "Price List";

    const lines = [];

    // Mimic your template header layout (rows similar to Excel)
    lines.push(csvEscape("")); // Row 1 blank (template had logo row)
    lines.push(
      [
        // Row 2
        csvEscape("Live Takeoff LLC"),
        csvEscape(""),
        csvEscape(`Price List Prepared for ${customer}`),
      ].join(","),
    );

    lines.push([csvEscape("750 SW 34th ST, Suite 209")].join(",")); // Row 3
    lines.push(
      [
        // Row 4
        csvEscape("Fort Lauderdale, FL 33315"),
        csvEscape(""),
        csvEscape(`Date: ${dateStr}`),
      ].join(","),
    );
    lines.push([csvEscape("www.livetakeoff.com")].join(",")); // Row 5
    lines.push([csvEscape("ops@livetakeoff.com | +1 855-500-0538")].join(",")); // Row 6

    lines.push(csvEscape("")); // Row 7 blank

    // Table header row (Row 8)
    const headerRow = [
      "Aircraft Type",
      ...exportPreview.services.map((s) => s.name),
    ].map(csvEscape);
    lines.push(headerRow.join(","));

    // Data rows
    for (const a of exportPreview.aircraft_types) {
      const row = [
        a.name,
        ...exportPreview.services.map((s) => {
          const raw = exportPreview?.prices?.[a.id]?.[s.id] ?? "";
          return priceForCsv(raw, false); // set true if you want $ in CSV
        }),
      ].map(csvEscape);

      lines.push(row.join(","));
    }

    const csv = lines.join("\n");

    const safeCustomer = (exportCustomerName?.trim() || "Customer")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, " ")
      .trim();

    const filename = `Pricing Proposal - ${safeCustomer} - ${dateStr} - ${pricePlanName}.csv`;

    downloadTextFile(filename, csv);
    toast.success("CSV downloaded.");
  };

  const searchAircrafts = async () => {
    setLoading(true);
    const request = { name: aircraftSearchName };
    const { data } = await api.getAircraftTypes(request);
    setTotalAircraftTypes(data.count);
    setAircraftTypes(data.results);
    setLoading(false);
  };

  const searchAircraftCategories = async () => {
    setLoading(true);
    const request = { name: aircraftCategorySearchName };
    const { data } = await api.getAircraftCategories(request);
    setTotalAircraftCategories(data.count);
    setAircraftCategories(data.results);
    setLoading(false);
  };

  const searchServices = async () => {
    setLoading(true);
    const request = { name: serviceSearchName };
    try {
      const { data } = await api.getServices(request);
      setServices(data.results);
      setTotalServices(data.count);
    } catch (err) {
      toast.error("Unable to get services");
    }
    setLoading(false);
  };

  const searchPricePlans = async () => {
    const response = await api.getPricingPlans();
    setPricePlans(response.data.results);

    const pricePlan = response.data.results.find(
      (item) => item.id === parseInt(pricePlanId),
    );

    if (pricePlan) {
      setPricePlansSelected([pricePlan]);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchAircrafts();
    }
  };

  const handleKeyDownCategories = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchAircraftCategories();
    }
  };

  const handleKeyDownServices = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchServices();
    }
  };

  const getAircraftCategoryDetails = async (aircraftCategory) => {
    const newAircraftCategories = aircraftCategories.map((item) => {
      if (item.id === aircraftCategory.id) item.showDetails = !item.showDetails;
      else item.showDetails = false;
      return item;
    });

    setAircraftCategories(newAircraftCategories);
    setAircraftCategorySelected(aircraftCategory);

    const request = {
      aircraft_category_id: aircraftCategory.id,
      price_list_ids: pricePlansSelected.map((item) => item.id),
    };

    const response = await api.getPricesListingByAircraftCategory(request);

    setPriceListing(response.data);
    setOriginalPriceListing(clonePriceListing(response.data));
  };

  const getAircraftDetails = async (aircraftType) => {
    const newAircraftTypes = aircraftTypes.map((item) => {
      if (item.id === aircraftType.id) item.showDetails = !item.showDetails;
      else item.showDetails = false;
      return item;
    });

    setAircraftTypes(newAircraftTypes);
    setAircraftTypeSelected(aircraftType);

    const request = {
      aircraft_type_id: aircraftType.id,
      price_list_ids: pricePlansSelected.map((item) => item.id),
    };

    const response = await api.getPricesListing(request);

    setPriceListing(response.data);
    setOriginalPriceListing(clonePriceListing(response.data));
  };

  const getServiceDetails = async (service) => {
    const newServices = services.map((item) => {
      if (item.id === service.id) item.showDetails = !item.showDetails;
      else item.showDetails = false;
      return item;
    });

    setServices(newServices);
    setServiceSelected(service);

    const request = {
      service_id: service.id,
      price_list_ids: pricePlansSelected.map((item) => item.id),
    };

    const response = await api.getPricesListingByService(request);

    setPriceListing(response.data);
    setOriginalPriceListing(clonePriceListing(response.data));
  };

  const updateServicePrice = (serviceName, priceListName, updatedPrice) => {
    const price = moneySanitize(updatedPrice);

    const updatedPriceListing = priceListing.map((priceList) => {
      if (priceList.service === serviceName) {
        const updatedPriceListEntries = priceList.price_list_entries.map(
          (entry) => {
            if (entry.price_list === priceListName) return { ...entry, price };
            return entry;
          },
        );
        return { ...priceList, price_list_entries: updatedPriceListEntries };
      }
      return priceList;
    });

    setPriceListing(updatedPriceListing);
  };

  const updateAircraftPrice = (
    aircraftTypeName,
    priceListName,
    updatedPrice,
  ) => {
    const price = moneySanitize(updatedPrice);

    const updatedPriceListing = priceListing.map((priceList) => {
      if (priceList.aircraft_type === aircraftTypeName) {
        const updatedPriceListEntries = priceList.price_list_entries.map(
          (entry) => {
            if (entry.price_list === priceListName) return { ...entry, price };
            return entry;
          },
        );
        return { ...priceList, price_list_entries: updatedPriceListEntries };
      }
      return priceList;
    });

    setPriceListing(updatedPriceListing);
  };

  const buildSaveConfirmation = (pricePlanName, mode) => {
    const changes = [];

    const priceListEntries = priceListing.map((priceList) => {
      const priceListEntry = priceList.price_list_entries.find(
        (entry) => entry.price_list === pricePlanName,
      );

      const rowKey =
        mode === "service" ? priceList.aircraft_type : priceList.service;

      const originalRow = originalPriceListing.find((item) =>
        mode === "service"
          ? item.aircraft_type === priceList.aircraft_type
          : item.service === priceList.service,
      );

      const originalPriceListEntry = originalRow?.price_list_entries?.find(
        (entry) => entry.price_list === pricePlanName,
      );

      const oldPrice = originalPriceListEntry?.price ?? "";
      const newPrice = priceListEntry?.price ?? "";

      if (!pricesAreEqual(oldPrice, newPrice)) {
        changes.push({
          label: rowKey,
          oldValue: oldPrice,
          newValue: newPrice,
          direction: getPriceDirection(oldPrice, newPrice),
        });
      }

      if (mode === "service") {
        return {
          aircraft_type: priceList.aircraft_type,
          price: newPrice,
          old_price: oldPrice,
        };
      }

      return {
        service: priceList.service,
        price: newPrice,
        old_price: oldPrice,
      };
    });

    if (changes.length === 0) {
      toast.info("There are no price changes to save.");
      return;
    }

    const request = {
      name: pricePlanName,
      price_list_entries: priceListEntries,
    };

    if (mode === "aircraft") {
      request.aircraft_type_id = aircraftTypeSelected?.id;
      request.aircraft_category_id = aircraftCategorySelected?.id;
    } else if (mode === "category") {
      request.aircraft_category_id = aircraftCategorySelected?.id;
    } else if (mode === "service") {
      request.service_id = serviceSelected?.id;
    }

    const contextLabel =
      mode === "aircraft"
        ? aircraftTypeSelected?.name
        : mode === "category"
          ? aircraftCategorySelected?.name
          : serviceSelected?.name;

    setPendingSave({
      mode,
      pricePlanName,
      request,
      changes,
      contextLabel,
    });

    setSaveConfirmationOpen(true);
  };

  const saveChangesForPricePlan = (pricePlanName) => {
    buildSaveConfirmation(pricePlanName, "aircraft");
  };

  const saveChangesForPricePlanByAircraftCategory = (pricePlanName) => {
    buildSaveConfirmation(pricePlanName, "category");
  };

  const saveChangesForPricePlanByService = (pricePlanName) => {
    buildSaveConfirmation(pricePlanName, "service");
  };

  const closeSaveConfirmation = () => {
    if (updateLoading) return;

    setSaveConfirmationOpen(false);
    setPendingSave(null);
  };

  const confirmSaveChanges = async () => {
    if (!pendingSave) return;

    setUpdateLoading(true);

    try {
      if (pendingSave.mode === "aircraft") {
        await api.updatePricePlan(
          aircraftTypeSelected?.id,
          pendingSave.request,
        );
      } else if (pendingSave.mode === "category") {
        await api.updatePricePlanByAircraftCategory(
          aircraftCategorySelected?.id,
          pendingSave.request,
        );
      } else {
        await api.updatePricePlanByService(
          serviceSelected?.id,
          pendingSave.request,
        );
      }

      // These values are now the persisted values.
      // Make them the new baseline for future edits.
      setOriginalPriceListing(clonePriceListing(priceListing));

      setSaveConfirmationOpen(false);
      setPendingSave(null);

      toast.success("Price list updated successfully");

      if (activityOpen) {
        await loadPriceListActivity(activityPage);
      }
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleGroupOptionChange = async (event) => {
    setGroupOptionSelected(event);
    setAircraftTypeSelected(null);
    setServiceSelected(null);
    setAircraftCategorySelected(null);

    setAircraftTypes((prev) => prev.map((x) => ({ ...x, showDetails: false })));
    setServices((prev) => prev.map((x) => ({ ...x, showDetails: false })));
    setAircraftCategories((prev) =>
      prev.map((x) => ({ ...x, showDetails: false })),
    );
  };

  const handleSetPricePlansSelected = async (event) => {
    setPricePlansSelected(event);

    // If the price plan changes while in preview, exit preview to avoid confusion
    if (previewMode) {
      setPreviewMode(false);
      setExportPreview(null);
    }

    const request = {
      aircraft_type_id: aircraftTypeSelected?.id,
      aircraft_category_id: aircraftCategorySelected?.id,
      service_id: serviceSelected?.id,
      price_list_ids: event.map((item) => item.id),
    };

    if (groupOptionSelected.name === "By Aircraft") {
      if (aircraftTypeSelected) {
        const response = await api.getPricesListing(request);
        setPriceListing(response.data);
        setOriginalPriceListing(clonePriceListing(response.data));
      }
    }

    if (groupOptionSelected.name === "By Aircraft Category") {
      if (aircraftCategorySelected) {
        const response = await api.getPricesListingByAircraftCategory(request);
        setPriceListing(response.data);
        setOriginalPriceListing(clonePriceListing(response.data));
      }
    }

    if (groupOptionSelected.name === "By Service") {
      if (serviceSelected) {
        const response = await api.getPricesListingByService(request);
        setPriceListing(response.data);
        setOriginalPriceListing(clonePriceListing(response.data));
      }
    }
  };

  // ===== Export modal helpers =====
  const openExportModal = async () => {
    if (!pricePlansSelected?.length) {
      toast.error("Select a price plan first.");
      return;
    }

    setExportOpen(true);
    setExportModalLoading(true);

    try {
      const [svcRes, acRes] = await Promise.all([
        api.getServices({ name: "" }),
        api.getAircraftTypes({ name: "" }),
      ]);
      setExportAllServices(svcRes.data.results || []);
      setExportAllAircraftTypes(acRes.data.results || []);
    } catch (e) {
      toast.error("Unable to load services/aircraft types for export.");
    } finally {
      setExportModalLoading(false);
    }
  };

  const closeExportModal = () => setExportOpen(false);

  // ===== Build preview using getExportPricesMatrix =====
  const handlePreview = async () => {
    if (!pricePlansSelected?.length)
      return toast.error("Select a price plan first.");
    if (!exportCustomerName.trim()) return toast.error("Enter Customer Name.");
    if (!exportServicesSelected.length)
      return toast.error("Select at least one service.");
    if (!exportAircraftSelected.length)
      return toast.error("Select at least one aircraft type.");

    const pricePlan = pricePlansSelected[0]; // proposal uses a single selected price plan

    setExportPreviewLoading(true);
    try {
      const request = {
        price_list_id: pricePlan.id,
        aircraft_type_ids: exportAircraftSelected.map((x) => x.id),
        service_ids: exportServicesSelected.map((x) => x.id),
      };

      const { data } = await api.getExportPricesMatrix(request);

      const aircraft = data.aircraft_types || [];
      const svcs = data.services || [];
      const list = data.price_list || {
        id: pricePlan.id,
        name: pricePlan.name,
      };

      // Build a full grid map [aircraftId][serviceId] => price string
      const grid = {};
      for (const a of aircraft) grid[a.id] = {};

      // Populate returned prices
      for (const p of data.prices || []) {
        if (!grid[p.aircraft_type_id]) grid[p.aircraft_type_id] = {};
        grid[p.aircraft_type_id][p.service_id] = moneySanitize(p.price);
      }

      // Ensure every cell exists (blank if missing)
      for (const a of aircraft) {
        for (const s of svcs) {
          if (grid[a.id][s.id] === undefined) grid[a.id][s.id] = "";
        }
      }

      setExportPreview({
        price_list: list,
        aircraft_types: aircraft,
        services: svcs,
        prices: grid,
      });

      setPreviewMode(true);
      setExportOpen(false);
      toast.success("Preview ready.");
    } catch (e) {
      toast.error("Unable to build preview (export matrix API failed).");
    } finally {
      setExportPreviewLoading(false);
    }
  };

  // frontend-only edit
  const updatePreviewPrice = (aircraftId, serviceId, newVal) => {
    setExportPreview((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        prices: {
          ...prev.prices,
          [aircraftId]: {
            ...(prev.prices?.[aircraftId] || {}),
            [serviceId]: moneySanitize(newVal),
          },
        },
      };
    });
  };

  const clonePriceListing = (listing = []) =>
    listing.map((item) => ({
      ...item,
      price_list_entries: (item.price_list_entries || []).map((entry) => ({
        ...entry,
      })),
    }));

  const isBlankPrice = (value) =>
    value === "" || value === null || value === undefined;

  const pricesAreEqual = (oldValue, newValue) => {
    if (isBlankPrice(oldValue) && isBlankPrice(newValue)) return true;
    if (isBlankPrice(oldValue) || isBlankPrice(newValue)) return false;

    const oldNumber = Number(oldValue);
    const newNumber = Number(newValue);

    if (!Number.isNaN(oldNumber) && !Number.isNaN(newNumber)) {
      return oldNumber === newNumber;
    }

    return String(oldValue) === String(newValue);
  };

  const getPriceDirection = (oldValue, newValue) => {
    if (isBlankPrice(oldValue) && !isBlankPrice(newValue)) return "up";
    if (!isBlankPrice(oldValue) && isBlankPrice(newValue)) return "down";

    const oldNumber = Number(oldValue);
    const newNumber = Number(newValue);

    if (Number.isNaN(oldNumber) || Number.isNaN(newNumber)) return "same";
    if (newNumber > oldNumber) return "up";
    if (newNumber < oldNumber) return "down";

    return "same";
  };

  const formatChangePrice = (value) => {
    if (isBlankPrice(value)) return "Not set";

    return formatCurrency(value) || String(value);
  };

  return (
    <div className="flex h-full -mt-8">
      {/* ===== Save Changes Confirmation Modal ===== */}
      <Transition.Root show={saveConfirmationOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[1000]"
          onClose={closeSaveConfirmation}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-950/60 backdrop-blur-[1px]" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
                  {/* Header */}
                  <div className="border-b border-gray-100 px-6 py-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gray-900 text-white shadow-sm">
                          <CheckIcon className="h-5 w-5" />
                        </div>

                        <div className="min-w-0">
                          <Dialog.Title className="text-lg font-semibold text-gray-950">
                            Confirm price changes
                          </Dialog.Title>

                          <p className="mt-1 text-sm leading-5 text-gray-500">
                            Review the changes below before updating{" "}
                            <span className="font-semibold text-gray-700">
                              {pendingSave?.pricePlanName}
                            </span>
                            .
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={closeSaveConfirmation}
                        disabled={updateLoading}
                        className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                        {pendingSave?.changes?.length || 0}{" "}
                        {(pendingSave?.changes?.length || 0) === 1
                          ? "change"
                          : "changes"}
                      </span>

                      {pendingSave?.contextLabel ? (
                        <span className="inline-flex max-w-full items-center rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600">
                          <span className="truncate">
                            {pendingSave.contextLabel}
                          </span>
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {/* Changes */}
                  <div className="px-6 py-5">
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Item
                        </span>

                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Price change
                        </span>
                      </div>

                      <div className="max-h-[420px] divide-y divide-gray-100 overflow-y-auto">
                        {(pendingSave?.changes || []).map((change, index) => (
                          <div
                            key={`${change.label}-${index}`}
                            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-6 px-4 py-3.5"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-gray-900">
                                {change.label}
                              </p>
                            </div>

                            <div className="flex items-center justify-end gap-2 text-sm tabular-nums">
                              <span className="font-medium text-gray-500">
                                {formatChangePrice(change.oldValue)}
                              </span>

                              <span className="text-gray-300">→</span>

                              <span
                                className={classNames(
                                  "rounded-md px-2 py-1 font-semibold",
                                  change.direction === "up"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : change.direction === "down"
                                      ? "bg-red-50 text-red-700"
                                      : "bg-gray-100 text-gray-700",
                                )}
                              >
                                {formatChangePrice(change.newValue)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <p className="mt-3 text-xs leading-5 text-gray-500">
                      Green indicates a price increase. Red indicates a price
                      decrease.
                    </p>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/80 px-6 py-4">
                    <button
                      type="button"
                      onClick={closeSaveConfirmation}
                      disabled={updateLoading}
                      className="inline-flex min-w-[96px] items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={confirmSaveChanges}
                      disabled={updateLoading}
                      className="inline-flex min-w-[112px] items-center justify-center rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {updateLoading ? "Saving..." : "Confirm"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* ===== Price List Activity Drawer ===== */}
      <Transition.Root show={activityOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[9999]"
          onClose={closePriceListActivity}
        >
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/20" />
          </Transition.Child>

          <div className="fixed inset-0 z-[9999] overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-xl">
                    <div className="flex h-full flex-col bg-white shadow-2xl">
                      {/* Header */}
                      <div className="border-b border-gray-200 px-6 py-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                              Price List Activity
                            </Dialog.Title>

                            <p className="mt-1 text-sm font-medium text-gray-500">
                              {pricePlansSelected?.[0]?.name || "Price List"}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={closePriceListActivity}
                            className="
                        rounded-lg p-2
                        text-gray-400
                        transition
                        hover:bg-gray-100
                        hover:text-gray-700
                      "
                          >
                            <span className="sr-only">Close activity</span>
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>

                      {/* Activity content */}
                      <div className="flex-1 overflow-y-auto px-6 py-5">
                        {activityLoading ? (
                          <div className="flex justify-center py-12">
                            <Loader />
                          </div>
                        ) : priceListActivities.length === 0 ? (
                          <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="h-6 w-6 text-gray-400"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>

                            <h3 className="mt-4 text-sm font-semibold text-gray-900">
                              No activity yet
                            </h3>

                            <p className="mt-1 max-w-xs text-sm text-gray-500">
                              Price changes made to this price list will appear
                              here.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-7">
                            {priceListActivities.map((activity) => (
                              <div key={activity.id} className="relative pl-8">
                                {/* Timeline line */}
                                <div className="absolute bottom-0 left-[11px] top-6 w-px bg-gray-200" />

                                {/* Timeline dot */}
                                <div className="absolute left-[7px] top-2 h-2.5 w-2.5 rounded-full bg-red-500 ring-4 ring-white" />

                                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                  <div className="flex items-start gap-3">
                                    {/* Actor initials */}
                                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                                      {activity.actor_name
                                        ?.split(" ")
                                        .map((part) => part[0])
                                        .join("")
                                        .substring(0, 2)
                                        .toUpperCase()}
                                    </div>

                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-baseline gap-x-1">
                                        <span className="text-sm font-semibold text-gray-900">
                                          {activity.actor_name}
                                        </span>

                                        <span className="text-sm text-gray-500">
                                          updated
                                        </span>

                                        <span className="text-sm font-semibold text-gray-700">
                                          {activity.changes_count}
                                        </span>

                                        <span className="text-sm text-gray-500">
                                          {activity.changes_count === 1
                                            ? "price"
                                            : "prices"}
                                        </span>
                                      </div>

                                      <div className="mt-1 text-xs text-gray-400">
                                        {new Date(
                                          activity.created_at,
                                        ).toLocaleString("en-US", {
                                          timeZone: "America/New_York",
                                          month: "short",
                                          day: "numeric",
                                          year: "numeric",
                                          hour: "numeric",
                                          minute: "2-digit",
                                          timeZoneName: "short",
                                        })}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Changes */}
                                  <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
                                    {activity.changes.map((change) => {
                                      const oldPrice = Number(change.old_value);
                                      const newPrice = Number(change.new_value);

                                      const increased = newPrice > oldPrice;
                                      const decreased = newPrice < oldPrice;

                                      return (
                                        <div
                                          key={change.id}
                                          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4"
                                        >
                                          <div className="min-w-0">
                                            <div className="truncate text-sm font-medium text-gray-700">
                                              {change.service_name}
                                            </div>

                                            {change.aircraft_type_name ? (
                                              <div className="mt-0.5 text-xs text-gray-400">
                                                {change.aircraft_type_name}
                                              </div>
                                            ) : null}

                                            {change.aircraft_category_name ? (
                                              <div className="mt-0.5 text-xs text-gray-400">
                                                {change.aircraft_category_name}
                                              </div>
                                            ) : null}
                                          </div>

                                          <div className="flex items-center gap-2 whitespace-nowrap text-sm tabular-nums">
                                            <span className="font-medium text-gray-500">
                                              {formatCurrency(change.old_value)}
                                            </span>

                                            <span className="text-gray-300">
                                              →
                                            </span>

                                            <span
                                              className={classNames(
                                                "font-semibold",
                                                increased
                                                  ? "text-emerald-600"
                                                  : decreased
                                                    ? "text-red-600"
                                                    : "text-gray-700",
                                              )}
                                            >
                                              {formatCurrency(change.new_value)}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {activity.note ? (
                                    <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                                      {activity.note}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Pagination */}
                      {activityTotalCount > 0 ? (
                        <div className="border-t border-gray-200 bg-white px-6 py-4">
                          {/* Replace this block with your existing LiveTakeoff pagination component */}
                          <div className="flex items-center justify-between gap-3">
                            <button
                              type="button"
                              disabled={activityPage === 1 || activityLoading}
                              onClick={() =>
                                setActivityPage((page) => Math.max(page - 1, 1))
                              }
                              className="
                          rounded-md border border-gray-300 bg-white
                          px-3 py-2 text-sm font-semibold text-gray-700
                          hover:bg-gray-50
                          disabled:cursor-not-allowed disabled:opacity-40
                        "
                            >
                              Previous
                            </button>

                            <span className="text-sm text-gray-500">
                              Page {activityPage} of{" "}
                              {Math.ceil(activityTotalCount / activityPageSize)}
                            </span>

                            <button
                              type="button"
                              disabled={
                                activityPage >=
                                  Math.ceil(
                                    activityTotalCount / activityPageSize,
                                  ) || activityLoading
                              }
                              onClick={() =>
                                setActivityPage((page) => page + 1)
                              }
                              className="
                          rounded-md border border-gray-300 bg-white
                          px-3 py-2 text-sm font-semibold text-gray-700
                          hover:bg-gray-50
                          disabled:cursor-not-allowed disabled:opacity-40
                        "
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* ===== Export Modal ===== */}
      <Transition.Root show={exportOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={setExportOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end sm:items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white shadow-xl ring-1 ring-black/5">
                  <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Export Pricing Proposal
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose the aircraft types and services to include.
                        You’ll preview prices before exporting.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded-lg p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                      onClick={closeExportModal}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="px-6 py-5 space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800">
                        Customer Name
                      </label>
                      <input
                        type="text"
                        value={exportCustomerName}
                        onChange={(e) => setExportCustomerName(e.target.value)}
                        placeholder="e.g., ABC Aviation Group"
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        This is only used in the proposal header (not tied to
                        your database).
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <MultiSelectDropdown
                        label="Services"
                        placeholder="Search & select services…"
                        items={exportAllServices}
                        selected={exportServicesSelected}
                        setSelected={setExportServicesSelected}
                        displayKey="name"
                        disabled={exportModalLoading || exportPreviewLoading}
                      />

                      <MultiSelectDropdown
                        label="Aircraft Types"
                        placeholder="Search & select aircraft types…"
                        items={exportAllAircraftTypes}
                        selected={exportAircraftSelected}
                        setSelected={setExportAircraftSelected}
                        displayKey="name"
                        disabled={exportModalLoading || exportPreviewLoading}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                      onClick={closeExportModal}
                      disabled={exportModalLoading || exportPreviewLoading}
                    >
                      Close
                    </button>

                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 disabled:opacity-60"
                      onClick={handlePreview}
                      disabled={exportModalLoading || exportPreviewLoading}
                    >
                      {exportModalLoading
                        ? "Loading..."
                        : exportPreviewLoading
                          ? "Building preview..."
                          : "Preview"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* ===== Side bar for mobile (your existing) ===== */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white focus:outline-none">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full
                                  focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white border-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex-shrink-0 border-t border-gray-200 p-4">
                  <div className="flex justify-between">
                    <h2 className="text-2xl font-medium text-gray-900">
                      Aircrafts
                    </h2>
                    <div></div>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    Search list of {totalAircraftTypes} aircrafts types
                  </p>
                  <form className="mt-6 flex space-x-4" action="#">
                    <div className="min-w-0 flex-1">
                      <label htmlFor="search" className="sr-only">
                        Search
                      </label>
                      <div className="relative rounded-md shadow-sm">
                        <div
                          onClick={() => searchAircrafts()}
                          className="absolute inset-y-0 left-0 flex items-center pl-3 cursor-pointer"
                        >
                          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 cursor-pointer" />
                        </div>
                        <input
                          type="search"
                          name="search"
                          id="search"
                          value={aircraftSearchName}
                          onChange={(event) =>
                            setAircraftSearchName(event.target.value)
                          }
                          onKeyDown={handleKeyDown}
                          className="block w-full rounded-md border-gray-300 pl-10 focus:border-sky-500
                                    focus:ring-sky-500 sm:text-sm"
                          placeholder="Search name..."
                        />
                      </div>
                    </div>
                  </form>

                  <nav
                    className="min-h-0 flex-1 overflow-y-auto mt-5"
                    style={{ height: "800px", paddingBottom: "250px" }}
                  >
                    {loading && <Loader />}

                    {!loading && totalAircraftTypes === 0 && (
                      <div className="text-gray-500 text-sm flex flex-col mt-20 text-center">
                        <p className="font-semibold">No aircrafts found.</p>
                        <p>
                          You can add an aircraft by clicking on the plus icon.
                        </p>
                      </div>
                    )}
                    <ul
                      role="list"
                      className="relative z-0 divide-y divide-gray-200"
                    >
                      {aircraftTypes.map((aircraft) => (
                        <li
                          key={aircraft.id}
                          onClick={() => getAircraftDetails(aircraft)}
                        >
                          <div
                            className={`${aircraft.showDetails ? " border-2 border-red-500" : ""} cursor-pointer relative flex items-center space-x-3 px-6 py-5 hover:bg-gray-50`}
                          >
                            <div className="min-w-0 flex-1">
                              <span
                                className="absolute inset-0"
                                aria-hidden="true"
                              />
                              <p className="text-sm font-medium text-gray-900">
                                {aircraft.name}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0" aria-hidden="true" />
          </div>
        </Dialog>
      </Transition.Root>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="relative z-0 flex flex-1 overflow-hidden">
          <main className="relative z-0 flex-1 overflow-y-auto focus:outline-none xl:order-last">
            {/* Mobile only */}
            <nav
              className="flex items-start px-4 py-3 sm:px-6 lg:px-8 xl:hidden"
              aria-label="Breadcrumb"
            >
              <div
                onClick={() => setSidebarOpen(true)}
                className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900"
              >
                <ChevronLeftIcon className="-ml-2 h-5 w-5 text-gray-400" />
                <span>Aircrafts</span>
              </div>
            </nav>

            <article className="m-auto max-w-full px-4">
              <div>
                {updateLoading && (
                  <div className="relative isolate flex items-center gap-x-6 overflow-hidden bg-gray-50 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
                    <p className="text-lg leading-6 py-2 text-gray-900 font-medium">
                      Updating open jobs...
                    </p>
                    <div className="flex flex-1 justify-end"></div>
                  </div>
                )}

                <div className="mx-auto max-w-full bg-white pb-16 sm:pb-16 lg:max-w-full pt-1">
                  <div style={{ minWidth: "820px" }}>
                    {/* ===== Top row: Price plan dropdown + Export button ===== */}
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <div style={{ width: "350px" }}>
                        <Listbox
                          multiple
                          value={pricePlansSelected}
                          onChange={handleSetPricePlansSelected}
                        >
                          {({ open }) => (
                            <div className="relative">
                              <Listbox.Button
                                className="relative w-full cursor-default rounded-md border
                                    border-gray-300 bg-white py-2 pl-3 pr-10 text-left
                                    shadow-sm focus:border-sky-500 focus:outline-none
                                    focus:ring-1 focus:ring-sky-500 text-md"
                              >
                                <span className="block truncate">
                                  {pricePlansSelected.length > 0 &&
                                    pricePlansSelected
                                      .map((p) => p.name)
                                      .join(", ")}
                                  {pricePlansSelected.length === 0 &&
                                    "Select price list"}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                </span>
                              </Listbox.Button>

                              <Transition
                                show={open}
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                              >
                                <Listbox.Options
                                  className="absolute left-0 z-50 mt-1 max-h-72 w-full overflow-auto
                                  rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-md"
                                >
                                  {pricePlans.map((pricePlan) => (
                                    <Listbox.Option
                                      key={pricePlan.id}
                                      className={({ active }) =>
                                        classNames(
                                          active
                                            ? "text-white bg-red-600"
                                            : "text-gray-900",
                                          "relative cursor-default select-none py-2 pl-3 pr-9",
                                        )
                                      }
                                      value={pricePlan}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span
                                            className={classNames(
                                              selected
                                                ? "font-semibold"
                                                : "font-normal",
                                              "block truncate",
                                            )}
                                          >
                                            {pricePlan.name}
                                          </span>
                                          {selected ? (
                                            <span
                                              className={classNames(
                                                active
                                                  ? "text-white"
                                                  : "text-red-600",
                                                "absolute inset-y-0 right-0 flex items-center pr-4",
                                              )}
                                            >
                                              <CheckIcon className="h-5 w-5" />
                                            </span>
                                          ) : null}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  ))}
                                </Listbox.Options>
                              </Transition>
                            </div>
                          )}
                        </Listbox>
                      </div>

                      <div className="flex items-center gap-2">
                        {previewMode ? (
                          <button
                            type="button"
                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            onClick={() => {
                              setPreviewMode(false);
                              setExportPreview(null);
                            }}
                          >
                            Exit preview
                          </button>
                        ) : null}

                        {!previewMode && (
                          <button
                            type="button"
                            onClick={openPriceListActivity}
                            disabled={!pricePlansSelected?.length}
                            className="
                                inline-flex items-center gap-2
                                rounded-md border border-gray-300
                                bg-white px-4 py-2
                                text-sm font-semibold text-gray-700
                                shadow-sm
                                transition
                                hover:bg-gray-50 hover:text-gray-900
                                disabled:cursor-not-allowed disabled:opacity-50
                                "
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="h-4 w-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Activity
                          </button>
                        )}

                        {!previewMode && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700"
                            onClick={openExportModal}
                          >
                            <ShareIcon className="h-4 w-4" />
                            Export
                          </button>
                        )}
                      </div>
                    </div>

                    {/* ===== Preview table ===== */}
                    {previewMode && exportPreview && (
                      <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              Proposal Preview
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Click any price cell to edit. Changes stay only in
                              frontend state.
                            </p>
                          </div>

                          <div className="text-right text-sm text-gray-600">
                            <div className="font-semibold text-gray-900">
                              {exportPreview.price_list?.name}
                            </div>
                            <div>{exportCustomerName}</div>
                          </div>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="sticky left-0 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-200">
                                  Aircraft Type
                                </th>
                                {exportPreview.services.map((s) => (
                                  <th
                                    key={s.id}
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-200"
                                  >
                                    {s.name}
                                  </th>
                                ))}
                              </tr>
                            </thead>

                            <tbody className="bg-white">
                              {exportPreview.aircraft_types.map((a) => (
                                <tr key={a.id} className="hover:bg-gray-50">
                                  <td className="sticky left-0 bg-white px-4 py-2 text-sm font-semibold text-gray-900 border-b border-gray-100">
                                    {a.name}
                                  </td>

                                  {exportPreview.services.map((s) => (
                                    <EditableCell
                                      key={s.id}
                                      value={
                                        exportPreview?.prices?.[a.id]?.[s.id] ??
                                        ""
                                      }
                                      onChange={(v) =>
                                        updatePreviewPrice(a.id, s.id, v)
                                      }
                                    />
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
                          <button
                            type="button"
                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            onClick={openExportModal}
                          >
                            Edit selections
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
                            onClick={exportToCsv}
                          >
                            Export CSV
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
                            onClick={exportToPdf}
                          >
                            Export PDF
                          </button>
                        </div>
                      </div>
                    )}

                    {groupOptionSelected.name === "By Aircraft" &&
                      aircraftTypeSelected === null && (
                        <div className="text-lg text-gray-700 mt-4">
                          Select an aircraft.
                        </div>
                      )}

                    {groupOptionSelected.name === "By Aircraft Category" &&
                      aircraftCategorySelected === null && (
                        <div className="text-lg text-gray-700 mt-4">
                          Select an aircraft category.
                        </div>
                      )}

                    {groupOptionSelected.name === "By Service" &&
                      serviceSelected === null && (
                        <div className="text-lg text-gray-700 mt-4">
                          Select a service.
                        </div>
                      )}

                    {groupOptionSelected.name === "By Aircraft" &&
                      aircraftTypeSelected !== null && (
                        <table className="mt-2 min-w-full divide-y divide-gray-300 table-fixed">
                          <thead>
                            <tr>
                              <th
                                className="pb-1 text-left text-sm font-medium text-gray-900"
                                scope="col"
                              >
                                Aircraft
                              </th>
                              {pricePlansSelected.map((pricePlan) => (
                                <th
                                  key={pricePlan.name}
                                  className="px-6 pb-1 text-left text-sm font-medium leading-6 text-gray-900"
                                  scope="col"
                                >
                                  {pricePlan.name} - {pricePlan.id}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-200 border-t border-gray-200">
                            {priceListing.map((entry) => (
                              <tr
                                key={entry.service}
                                className="hover:bg-gray-100"
                              >
                                <th
                                  className="py-2 text-left text-sm font-normal text-gray-600"
                                  style={{ width: "500px" }}
                                  scope="row"
                                >
                                  {entry.service}
                                </th>

                                {entry.price_list_entries.map((priceList) => (
                                  <td
                                    key={priceList.price_list}
                                    className="py-1 px-6"
                                  >
                                    <input
                                      type="text"
                                      name="price"
                                      id="price"
                                      value={priceList.price}
                                      onChange={(e) =>
                                        updateServicePrice(
                                          entry.service,
                                          priceList.price_list,
                                          e.target.value,
                                        )
                                      }
                                      style={{ width: "80px" }}
                                      className="block border-gray-300 py-2 focus:border-gray-500 focus:ring-gray-500 text-xs"
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>

                          {(currentUser.isAdmin ||
                            currentUser.isSuperUser ||
                            currentUser.isAccountManager) && (
                            <tfoot>
                              <tr className="border-t border-gray-200">
                                <th className="sr-only" scope="row"></th>
                                {pricePlansSelected.map((pricePlan) => (
                                  <td
                                    key={pricePlan.name}
                                    className="px-6 pt-5"
                                  >
                                    <button
                                      disabled={updateLoading}
                                      onClick={() =>
                                        saveChangesForPricePlan(pricePlan.name)
                                      }
                                      className="block w-24 rounded-md border border-transparent bg-red-500 py-2 px-1 text-center text-xs font-semibold text-white shadow hover:to-pink-600"
                                    >
                                      Save Changes
                                    </button>
                                  </td>
                                ))}
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      )}

                    {groupOptionSelected.name === "By Aircraft Category" &&
                      aircraftCategorySelected !== null && (
                        <table className="mt-2 min-w-full divide-y divide-gray-300 table-fixed">
                          <thead>
                            <tr>
                              <th
                                className="pb-1 text-left text-sm font-medium text-gray-900"
                                scope="col"
                              >
                                Service
                              </th>
                              {pricePlansSelected.map((pricePlan) => (
                                <th
                                  key={pricePlan.name}
                                  className="px-6 pb-1 text-left text-sm font-medium leading-6 text-gray-900"
                                  scope="col"
                                >
                                  {pricePlan.name} - {pricePlan.id}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-200 border-t border-gray-200">
                            {priceListing.map((entry) => (
                              <tr
                                key={entry.service}
                                className="hover:bg-gray-100"
                              >
                                <th
                                  className="py-2 text-left text-sm font-normal text-gray-600"
                                  style={{ width: "500px" }}
                                  scope="row"
                                >
                                  {entry.service}
                                </th>

                                {entry.price_list_entries.map((priceList) => (
                                  <td
                                    key={priceList.price_list}
                                    className="py-1 px-6"
                                  >
                                    <input
                                      type="text"
                                      name="price"
                                      id="price"
                                      value={priceList.price}
                                      onChange={(e) =>
                                        updateServicePrice(
                                          entry.service,
                                          priceList.price_list,
                                          e.target.value,
                                        )
                                      }
                                      style={{ width: "80px" }}
                                      className="block border-gray-300 py-2 focus:border-gray-500 focus:ring-gray-500 text-xs"
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>

                          {(currentUser.isAdmin ||
                            currentUser.isSuperUser ||
                            currentUser.isAccountManager) && (
                            <tfoot>
                              <tr className="border-t border-gray-200">
                                <th className="sr-only" scope="row"></th>
                                {pricePlansSelected.map((pricePlan) => (
                                  <td
                                    key={pricePlan.name}
                                    className="px-6 pt-5"
                                  >
                                    <button
                                      disabled={updateLoading}
                                      onClick={() =>
                                        saveChangesForPricePlanByAircraftCategory(
                                          pricePlan.name,
                                        )
                                      }
                                      className="block w-24 rounded-md border border-transparent bg-red-500 py-2 px-1 text-center text-xs font-semibold text-white shadow hover:to-pink-600"
                                    >
                                      Save Changes
                                    </button>
                                  </td>
                                ))}
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      )}

                    {groupOptionSelected.name === "By Service" &&
                      serviceSelected !== null && (
                        <table className="mt-2 min-w-full divide-y divide-gray-300 table-fixed">
                          <thead>
                            <tr>
                              <th
                                className="pb-1 text-left text-sm font-medium text-gray-900"
                                scope="col"
                              >
                                Service
                              </th>
                              {pricePlansSelected.map((pricePlan) => (
                                <th
                                  key={pricePlan.name}
                                  className="px-6 pb-1 text-left text-sm font-medium leading-6 text-gray-900"
                                  scope="col"
                                >
                                  {pricePlan.name} - {pricePlan.id}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-200 border-t border-gray-200">
                            {priceListing.map((entry) => (
                              <tr
                                key={entry.aircraft_type}
                                className="hover:bg-gray-100"
                              >
                                <th
                                  className="py-2 text-left text-sm font-normal text-gray-600"
                                  style={{ width: "500px" }}
                                  scope="row"
                                >
                                  {entry.aircraft_type}
                                </th>

                                {entry.price_list_entries.map((priceList) => (
                                  <td
                                    key={priceList.price_list}
                                    className="py-1 px-6"
                                  >
                                    <input
                                      type="text"
                                      name="price"
                                      id="price"
                                      value={priceList.price}
                                      onChange={(e) =>
                                        updateAircraftPrice(
                                          entry.aircraft_type,
                                          priceList.price_list,
                                          e.target.value,
                                        )
                                      }
                                      style={{ width: "80px" }}
                                      className="block border-gray-300 py-2 focus:border-gray-500 focus:ring-gray-500 text-xs"
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>

                          <tfoot>
                            <tr className="border-t border-gray-200">
                              <th className="sr-only" scope="row"></th>
                              {pricePlansSelected.map((pricePlan) => (
                                <td key={pricePlan.name} className="px-6 pt-5">
                                  <button
                                    disabled={updateLoading}
                                    onClick={() =>
                                      saveChangesForPricePlanByService(
                                        pricePlan.name,
                                      )
                                    }
                                    className="block w-24 rounded-md border border-transparent bg-red-500 py-2 px-1 text-center text-xs font-semibold text-white shadow hover:to-pink-600"
                                  >
                                    Save Changes
                                  </button>
                                </td>
                              ))}
                            </tr>
                          </tfoot>
                        </table>
                      )}
                  </div>
                </div>
              </div>
            </article>
          </main>

          <aside className="hidden w-72 flex-shrink-0 border-r border-gray-200 xl:order-first xl:flex xl:flex-col">
            <div className="px-2 pt-1 pb-4">
              <RadioGroup
                value={groupOptionSelected}
                onChange={(e) => handleGroupOptionChange(e)}
              >
                <div className="mt-1 grid grid-cols-1 gap-y-2 sm:grid-cols-1 sm:gap-x-4">
                  {groupOptions.map((groupOption) => (
                    <RadioGroup.Option
                      key={groupOption.name}
                      value={groupOption}
                      className={({ active }) =>
                        classNames(
                          active
                            ? "border-red-500 ring-2 ring-red-500"
                            : "border-gray-300",
                          "relative cursor-pointer rounded-lg border bg-white p-3 shadow-sm focus:outline-none text-center",
                        )
                      }
                    >
                      {({ checked, active }) => (
                        <>
                          <span className="text-center">
                            <span className="flex flex-col">
                              <RadioGroup.Label
                                as="span"
                                className="block text-md font-medium text-gray-900"
                              >
                                {groupOption.name}
                              </RadioGroup.Label>
                            </span>
                          </span>
                          <span
                            className={classNames(
                              active ? "border" : "border-2",
                              checked ? "border-red-600" : "border-transparent",
                              "pointer-events-none absolute -inset-px rounded-lg",
                            )}
                            aria-hidden="true"
                          />
                        </>
                      )}
                    </RadioGroup.Option>
                  ))}
                </div>
              </RadioGroup>

              <div className="flex justify-between mt-4">
                <h2 className="text-2xl font-medium text-gray-900">
                  {groupOptionSelected.name === "By Aircraft" && "Aircrafts"}
                  {groupOptionSelected.name === "By Service" && "Services"}
                  {groupOptionSelected.name === "By Aircraft Category" &&
                    "Aircraft Categories"}
                </h2>
              </div>

              <form className="mt-2 flex space-x-4" action="#">
                <div className="min-w-0 flex-1">
                  <label htmlFor="search" className="sr-only">
                    Search
                  </label>

                  {groupOptionSelected.name === "By Aircraft" && (
                    <div className="relative rounded-md shadow-sm">
                      <div
                        onClick={() => searchAircrafts()}
                        className="absolute inset-y-0 left-0 flex items-center pl-3 cursor-pointer"
                      >
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 cursor-pointer" />
                      </div>
                      <input
                        type="search"
                        name="search"
                        id="search"
                        value={aircraftSearchName}
                        onChange={(event) =>
                          setAircraftSearchName(event.target.value)
                        }
                        onKeyDown={handleKeyDown}
                        className="block w-full rounded-md border-gray-300 pl-10 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                        placeholder="Search name..."
                      />
                    </div>
                  )}

                  {groupOptionSelected.name === "By Aircraft Category" && (
                    <div className="relative rounded-md shadow-sm">
                      <div
                        onClick={() => searchAircraftCategories()}
                        className="absolute inset-y-0 left-0 flex items-center pl-3 cursor-pointer"
                      >
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 cursor-pointer" />
                      </div>
                      <input
                        type="search"
                        name="search"
                        id="search"
                        value={aircraftCategorySearchName}
                        onChange={(event) =>
                          setAircraftCategorySearchName(event.target.value)
                        }
                        onKeyDown={handleKeyDownCategories}
                        className="block w-full rounded-md border-gray-300 pl-10 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                        placeholder="Search category name..."
                      />
                    </div>
                  )}

                  {groupOptionSelected.name === "By Service" && (
                    <div className="relative rounded-md shadow-sm">
                      <div
                        onClick={() => searchServices()}
                        className="absolute inset-y-0 left-0 flex items-center pl-3 cursor-pointer"
                      >
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 cursor-pointer" />
                      </div>
                      <input
                        type="search"
                        name="search"
                        id="search"
                        value={serviceSearchName}
                        onChange={(event) =>
                          setServiceSearchName(event.target.value)
                        }
                        onKeyDown={handleKeyDownServices}
                        className="block w-full rounded-md border-gray-300 pl-10 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                        placeholder="Search name..."
                      />
                    </div>
                  )}
                </div>
              </form>
            </div>

            <nav
              className="overflow-y-auto"
              style={{ height: "1000px", paddingBottom: "100px" }}
            >
              {loading && <Loader />}

              {groupOptionSelected.name === "By Aircraft" &&
                !loading &&
                totalAircraftTypes === 0 && (
                  <div className="text-gray-500 text-sm flex flex-col mt-20 text-center">
                    <p className="font-semibold">No aircrafts found.</p>
                  </div>
                )}

              {groupOptionSelected.name === "By Aircraft Category" &&
                !loading &&
                totalAircraftCategories === 0 && (
                  <div className="text-gray-500 text-sm flex flex-col mt-20 text-center">
                    <p className="font-semibold">
                      No aircrafts categories found.
                    </p>
                  </div>
                )}

              {groupOptionSelected.name === "By Service" &&
                !loading &&
                totalServices === 0 && (
                  <div className="text-gray-500 text-sm flex flex-col mt-20 text-center">
                    <p className="font-semibold">No services found.</p>
                  </div>
                )}

              {groupOptionSelected.name === "By Aircraft" && (
                <ul className="relative z-0 divide-y divide-gray-200">
                  {aircraftTypes.map((aircraft) => (
                    <li
                      key={aircraft.id}
                      onClick={() => getAircraftDetails(aircraft)}
                    >
                      <div
                        className={`${aircraft.showDetails ? " border-2 border-red-500" : ""} cursor-pointer relative flex items-center space-x-3 px-4 py-3 hover:bg-gray-100`}
                      >
                        <div className="min-w-0 flex-1">
                          <span
                            className="absolute inset-0"
                            aria-hidden="true"
                          />
                          <p className="text-sm text-gray-900">
                            {aircraft.name}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {groupOptionSelected.name === "By Aircraft Category" && (
                <ul className="relative z-0 divide-y divide-gray-200">
                  {aircraftCategories.map((category) => (
                    <li
                      key={category.id}
                      onClick={() => getAircraftCategoryDetails(category)}
                    >
                      <div
                        className={`${category.showDetails ? " border-2 border-red-500" : ""} cursor-pointer relative flex items-center space-x-3 px-4 py-3 hover:bg-gray-100`}
                      >
                        <div className="min-w-0 flex-1">
                          <span
                            className="absolute inset-0"
                            aria-hidden="true"
                          />
                          <p className="text-sm text-gray-900">
                            {category.name}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {groupOptionSelected.name === "By Service" && (
                <ul className="relative z-0 divide-y divide-gray-200">
                  {services.map((service) => (
                    <li
                      key={service.id}
                      onClick={() => getServiceDetails(service)}
                    >
                      <div
                        className={`${service.showDetails ? " border-2 border-red-500" : ""} cursor-pointer relative flex items-center space-x-3 px-4 py-3 hover:bg-gray-100`}
                      >
                        <div className="min-w-0 flex-1">
                          <span
                            className="absolute inset-0"
                            aria-hidden="true"
                          />
                          <p className="text-sm text-gray-900">
                            {service.name}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </nav>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ServicePrices;
